import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, 
  Bot, 
  User, 
  FileText, 
  Paperclip,
  Trash2,
  Download,
  Copy,
  Crown,
  Zap,
  Sparkles
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  relatedNotes?: string[];
  relatedDocuments?: string[];
  aiType?: 'standard' | 'premium';
}

const Chat: React.FC = () => {
  const { colors } = useTheme();
  const { user, canUseAI, incrementAIUsage } = useAuth();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: 'Hello! I\'m your AI study assistant. I can help you with your notes, answer questions about your documents, and assist with your learning journey. What would you like to know?',
      isUser: false,
      timestamp: new Date(),
      relatedNotes: [],
      relatedDocuments: [],
      aiType: 'standard'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedAIType, setSelectedAIType] = useState<'standard' | 'premium'>('standard');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const mockNotes = [
    { id: '1', title: 'React Fundamentals' },
    { id: '2', title: 'JavaScript ES6' },
    { id: '3', title: 'State Management' }
  ];

  const mockDocuments = [
    { id: '1', name: 'React Best Practices.pdf' },
    { id: '2', name: 'JavaScript Guide.docx' }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const simulateAIResponse = (userMessage: string, aiType: 'standard' | 'premium'): Message => {
    const responses = [
      {
        content: aiType === 'premium' 
          ? 'Based on your React notes, I can provide an advanced analysis of component patterns. Premium AI offers deeper insights: Would you like me to explain the differences between controlled and uncontrolled components with performance implications and best practices for enterprise applications?'
          : 'Based on your React notes, I can see you\'re learning about component patterns. Would you like me to explain the differences between controlled and uncontrolled components?',
        relatedNotes: ['1', '2']
      },
      {
        content: aiType === 'premium'
          ? 'I found comprehensive information in your JavaScript documentation with advanced context analysis. Here are the key concepts about async/await with performance optimization strategies and error handling patterns that might help with your current project.'
          : 'I found some relevant information in your JavaScript documentation. Here are the key concepts about async/await that might help with your current project.',
        relatedDocuments: ['2']
      },
      {
        content: aiType === 'premium'
          ? 'Excellent question! Let me provide a detailed breakdown with advanced insights based on your study materials. I\'ll reference your notes on state management to provide a comprehensive answer with real-world examples and performance considerations.'
          : 'Great question! Let me break this down based on your study materials. I\'ll reference your notes on state management to provide a comprehensive answer.',
        relatedNotes: ['3']
      }
    ];

    const randomResponse = responses[Math.floor(Math.random() * responses.length)];
    
    return {
      id: Date.now().toString(),
      content: randomResponse.content,
      isUser: false,
      timestamp: new Date(),
      relatedNotes: randomResponse.relatedNotes || [],
      relatedDocuments: randomResponse.relatedDocuments || [],
      aiType: aiType
    };
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Check if user can use AI
    if (!canUseAI(selectedAIType)) {
      const limitMessage = selectedAIType === 'premium' 
        ? 'Has alcanzado el límite diario de consultas IA Premium. Usa IA estándar o actualiza a Unlimited.'
        : 'Has alcanzado el límite diario de consultas IA. Actualiza tu plan para más consultas.';
      alert(limitMessage);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage,
      isUser: true,
      timestamp: new Date(),
      relatedNotes: [],
      relatedDocuments: []
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Increment AI usage
    incrementAIUsage(selectedAIType);

    // Simulate AI response delay
    setTimeout(() => {
      const aiResponse = simulateAIResponse(inputMessage, selectedAIType);
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, selectedAIType === 'premium' ? 1000 : 1500); // Premium is faster
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      id: '1',
      content: 'Hello! I\'m your AI study assistant. I can help you with your notes, answer questions about your documents, and assist with your learning journey. What would you like to know?',
      isUser: false,
      timestamp: new Date(),
      relatedNotes: [],
      relatedDocuments: [],
      aiType: 'standard'
    }]);
  };

  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const exportChat = () => {
    const chatContent = messages.map(msg => 
      `${msg.isUser ? 'You' : 'AI'} (${msg.timestamp.toLocaleString()}): ${msg.content}`
    ).join('\n\n');
    
    const blob = new Blob([chatContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chat-export.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const isPremium = user?.subscription.plan === 'premium';
  const isUnlimited = user?.subscription.plan === 'unlimited';
  const canUsePremium = isPremium || isUnlimited;

  const getAIQueriesLeft = (type: 'standard' | 'premium') => {
    if (isUnlimited) return -1; // unlimited
    if (type === 'premium') {
      if (!canUsePremium) return 0;
      return isPremium ? (20 - (user?.subscription.usage.premiumQueriesUsed || 0)) : -1;
    } else {
      if (user?.subscription.plan === 'free') {
        return 10 - (user?.subscription.usage.aiQueriesUsed || 0);
      }
      return -1; // unlimited for premium users
    }
  };

  const standardQueriesLeft = getAIQueriesLeft('standard');
  const premiumQueriesLeft = getAIQueriesLeft('premium');

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
            AI Chat
          </h1>
          <div className="flex items-center gap-4 mt-2">
            <p style={{ color: colors.subtext0 }}>
              Chat with your notes and documents using AI
            </p>
            
            {/* AI Type Selector */}
            <div 
              className="flex items-center rounded-lg border"
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: colors.surface0
              }}
            >
              <button
                onClick={() => setSelectedAIType('standard')}
                disabled={!canUseAI('standard')}
                className={`px-3 py-1 rounded-l-lg transition-colors text-sm flex items-center gap-1 ${
                  selectedAIType === 'standard' ? 'text-white' : ''
                } disabled:opacity-50`}
                style={{
                  backgroundColor: selectedAIType === 'standard' ? colors.green : 'transparent',
                  color: selectedAIType === 'standard' ? 'white' : colors.text
                }}
              >
                <Zap className="w-3 h-3" />
                Estándar
              </button>
              <button
                onClick={() => setSelectedAIType('premium')}
                disabled={!canUseAI('premium')}
                className={`px-3 py-1 rounded-r-lg transition-colors text-sm flex items-center gap-1 ${
                  selectedAIType === 'premium' ? 'text-white' : ''
                } disabled:opacity-50`}
                style={{
                  backgroundColor: selectedAIType === 'premium' ? colors.yellow : 'transparent',
                  color: selectedAIType === 'premium' ? 'white' : colors.text
                }}
              >
                <Sparkles className="w-3 h-3" />
                Premium
              </button>
            </div>

            {/* Usage Display */}
            <div className="flex items-center gap-2">
              {standardQueriesLeft !== -1 && (
                <div 
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style={{ 
                    backgroundColor: colors.surface0,
                    color: colors.text
                  }}
                >
                  <Zap className="w-3 h-3" style={{ color: colors.green }} />
                  {standardQueriesLeft} estándar
                </div>
              )}
              
              {canUsePremium && premiumQueriesLeft !== -1 && (
                <div 
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style={{ 
                    backgroundColor: colors.surface0,
                    color: colors.text
                  }}
                >
                  <Sparkles className="w-3 h-3" style={{ color: colors.yellow }} />
                  {premiumQueriesLeft} premium
                </div>
              )}

              {isUnlimited && (
                <div 
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style={{ 
                    backgroundColor: colors.yellow + '20',
                    color: colors.yellow
                  }}
                >
                  <Crown className="w-3 h-3" />
                  Ilimitadas
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={exportChat}
            className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          
          <button 
            onClick={clearChat}
            className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.red
            }}
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      {/* Usage Warnings */}
      {!isUnlimited && (
        <>
          {/* Standard AI Warning */}
          {user?.subscription.plan === 'free' && standardQueriesLeft <= 2 && standardQueriesLeft > 0 && (
            <div 
              className="p-4 rounded-lg border"
              style={{ 
                backgroundColor: colors.yellow + '10',
                borderColor: colors.yellow
              }}
            >
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5" style={{ color: colors.yellow }} />
                <div>
                  <p className="font-medium" style={{ color: colors.text }}>
                    Pocas consultas estándar restantes
                  </p>
                  <p className="text-sm" style={{ color: colors.subtext0 }}>
                    Te quedan {standardQueriesLeft} consultas IA estándar hoy. Actualiza para más consultas.
                  </p>
                </div>
                <button 
                  onClick={() => window.location.href = '/subscription'}
                  className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: colors.blue }}
                >
                  Actualizar
                </button>
              </div>
            </div>
          )}

          {/* Premium AI Warning */}
          {isPremium && premiumQueriesLeft <= 2 && premiumQueriesLeft > 0 && (
            <div 
              className="p-4 rounded-lg border"
              style={{ 
                backgroundColor: colors.yellow + '10',
                borderColor: colors.yellow
              }}
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5" style={{ color: colors.yellow }} />
                <div>
                  <p className="font-medium" style={{ color: colors.text }}>
                    Pocas consultas premium restantes
                  </p>
                  <p className="text-sm" style={{ color: colors.subtext0 }}>
                    Te quedan {premiumQueriesLeft} consultas IA premium hoy. Actualiza a Unlimited para consultas ilimitadas.
                  </p>
                </div>
                <button 
                  onClick={() => window.location.href = '/subscription'}
                  className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: colors.yellow }}
                >
                  Unlimited
                </button>
              </div>
            </div>
          )}

          {/* Limit Reached Warnings */}
          {((user?.subscription.plan === 'free' && standardQueriesLeft <= 0) || 
            (isPremium && selectedAIType === 'premium' && premiumQueriesLeft <= 0)) && (
            <div 
              className="p-4 rounded-lg border"
              style={{ 
                backgroundColor: colors.red + '10',
                borderColor: colors.red
              }}
            >
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5" style={{ color: colors.red }} />
                <div>
                  <p className="font-medium" style={{ color: colors.text }}>
                    Límite diario alcanzado
                  </p>
                  <p className="text-sm" style={{ color: colors.subtext0 }}>
                    Has usado todas tus consultas {selectedAIType === 'premium' ? 'premium' : 'estándar'} de hoy. 
                    {selectedAIType === 'premium' && isPremium ? ' Puedes usar IA estándar ilimitada.' : ' Actualiza tu plan para más consultas.'}
                  </p>
                </div>
                <button 
                  onClick={() => window.location.href = '/subscription'}
                  className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: colors.blue }}
                >
                  Actualizar Plan
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Chat Container */}
      <div 
        className="flex-1 rounded-xl border flex flex-col overflow-hidden"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-4 ${message.isUser ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {/* Avatar */}
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ 
                  backgroundColor: message.isUser ? colors.blue : 
                    message.aiType === 'premium' ? colors.yellow : colors.green
                }}
              >
                {message.isUser ? (
                  <User className="w-5 h-5 text-white" />
                ) : message.aiType === 'premium' ? (
                  <Sparkles className="w-5 h-5 text-white" />
                ) : (
                  <Bot className="w-5 h-5 text-white" />
                )}
              </div>

              {/* Message Content */}
              <div className={`flex-1 max-w-3xl ${message.isUser ? 'text-right' : 'text-left'}`}>
                <div
                  className={`inline-block p-4 rounded-xl ${
                    message.isUser ? 'rounded-tr-sm' : 'rounded-tl-sm'
                  }`}
                  style={{ 
                    backgroundColor: message.isUser ? colors.blue : colors.base,
                    color: message.isUser ? 'white' : colors.text
                  }}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  
                  {/* AI Type Badge */}
                  {!message.isUser && message.aiType && (
                    <div className="mt-2 pt-2 border-t border-opacity-20" style={{ borderColor: 'currentColor' }}>
                      <span 
                        className="text-xs px-2 py-1 rounded flex items-center gap-1 w-fit"
                        style={{ 
                          backgroundColor: message.aiType === 'premium' ? colors.yellow + '20' : colors.green + '20',
                          color: message.aiType === 'premium' ? colors.yellow : colors.green
                        }}
                      >
                        {message.aiType === 'premium' ? <Sparkles className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                        IA {message.aiType === 'premium' ? 'Premium' : 'Estándar'}
                      </span>
                    </div>
                  )}
                  
                  {/* Related Notes and Documents */}
                  {(message.relatedNotes?.length || message.relatedDocuments?.length) && (
                    <div className="mt-3 pt-3 border-t border-opacity-20" style={{ borderColor: 'currentColor' }}>
                      {message.relatedNotes && message.relatedNotes.length > 0 && (
                        <div className="mb-2">
                          <p className="text-sm opacity-80 mb-1">Related Notes:</p>
                          <div className="flex flex-wrap gap-2">
                            {message.relatedNotes.map((noteId) => {
                              const note = mockNotes.find(n => n.id === noteId);
                              return note ? (
                                <span
                                  key={noteId}
                                  className="text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80"
                                  style={{ 
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: 'currentColor'
                                  }}
                                >
                                  <FileText className="w-3 h-3 inline mr-1" />
                                  {note.title}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {message.relatedDocuments && message.relatedDocuments.length > 0 && (
                        <div>
                          <p className="text-sm opacity-80 mb-1">Related Documents:</p>
                          <div className="flex flex-wrap gap-2">
                            {message.relatedDocuments.map((docId) => {
                              const doc = mockDocuments.find(d => d.id === docId);
                              return doc ? (
                                <span
                                  key={docId}
                                  className="text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80"
                                  style={{ 
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: 'currentColor'
                                  }}
                                >
                                  <Paperclip className="w-3 h-3 inline mr-1" />
                                  {doc.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs" style={{ color: colors.subtext0 }}>
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                  
                  {!message.isUser && (
                    <button
                      onClick={() => copyMessage(message.content)}
                      className="p-1 rounded hover:opacity-80"
                      style={{ color: colors.subtext0 }}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex gap-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: selectedAIType === 'premium' ? colors.yellow : colors.green }}
              >
                {selectedAIType === 'premium' ? (
                  <Sparkles className="w-5 h-5 text-white" />
                ) : (
                  <Bot className="w-5 h-5 text-white" />
                )}
              </div>
              
              <div
                className="p-4 rounded-xl rounded-tl-sm"
                style={{ 
                  backgroundColor: colors.base,
                  color: colors.text
                }}
              >
                <div className="flex gap-1">
                  <div 
                    className="w-2 h-2 rounded-full animate-bounce"
                    style={{ backgroundColor: colors.subtext0, animationDelay: '0ms' }}
                  />
                  <div 
                    className="w-2 h-2 rounded-full animate-bounce"
                    style={{ backgroundColor: colors.subtext0, animationDelay: '150ms' }}
                  />
                  <div 
                    className="w-2 h-2 rounded-full animate-bounce"
                    style={{ backgroundColor: colors.subtext0, animationDelay: '300ms' }}
                  />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div 
          className="p-4 border-t"
          style={{ 
            backgroundColor: colors.base,
            borderColor: colors.surface1
          }}
        >
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  !canUseAI(selectedAIType)
                    ? `Límite diario de IA ${selectedAIType} alcanzado...`
                    : `Pregunta usando IA ${selectedAIType}...`
                }
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
                style={{
                  backgroundColor: colors.mantle,
                  borderColor: colors.surface0,
                  color: colors.text
                }}
                disabled={isTyping || !canUseAI(selectedAIType)}
              />
            </div>
            
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isTyping || !canUseAI(selectedAIType)}
              className="px-4 py-3 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: selectedAIType === 'premium' ? colors.yellow : colors.green }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs" style={{ color: colors.subtext0 }}>
              Press Enter to send, Shift+Enter for new line
            </p>
            
            <div className="flex items-center gap-2 text-xs" style={{ color: colors.subtext0 }}>
              <span>Connected to {mockNotes.length} notes, {mockDocuments.length} documents</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;